import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { View } from '../../types';
import { Capacitor } from '@capacitor/core';
import { BreederDashboard } from './BreederDashboard';
import { BreederPortalHome } from './BreederPortalHome';
import { LoginView, RegisterView } from '../../components/AuthViews';
import {
    ShieldCheck, ArrowLeft, Heart,
    Zap, CheckCircle2, ArrowRight, Package, MapPin,
    Activity, Mail, LogOut,
    Users, Calendar, ChevronRight, QrCode
} from 'lucide-react';

interface BreederPortalViewProps {
    onNavigate: (view: View) => void;
}

export const BreederPortalView: React.FC<BreederPortalViewProps> = ({ onNavigate }) => {
    const { user, logout } = useAuth();
    // En la web, quien entra a /criaderos sin sesión ve primero la portada; en la app
    // (APK de criadero) se va directo al ingreso.
    const [activePortalTab, setActivePortalTab] = useState<'discover' | 'login' | 'register'>(Capacitor.isNativePlatform() ? 'login' : 'discover');

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const action = params.get('action');
        if (action === 'login') {
            setActivePortalTab('login');
        } else if (action === 'register' || params.get('role') === 'breeder') {
            setActivePortalTab('register');
        }
    }, []);

    const handleInnerNavigate = (view: View) => {
        if (view === View.REGISTER) {
            setActivePortalTab('register');
        } else if (view === View.LOGIN) {
            setActivePortalTab('login');
        } else if (view === View.LANDING || view === View.BREEDER_DASHBOARD) {
            setActivePortalTab('discover');
        } else {
            onNavigate(view);
        }
    };

    const handleLogout = () => {
        if (confirm("¿Estás seguro de que quieres cerrar sesión del Portal de Criadores?")) {
            logout();
            setActivePortalTab('login');
        }
    };

    // Criador por rol propio o por solicitud de socio aprobada (acceso multi-portal, como en App.tsx).
    const isBreeder = !!user && (user.role?.includes('breeder') || (user as any).partner_roles?.includes('breeder') || user.role?.includes('root'));

    // CASE 1: Breeder is Logged In
    if (user && isBreeder) {
        return (
            <div className="min-h-screen bg-slate-50 w-full animate-in fade-in duration-500">
                <BreederDashboard onExit={handleLogout} />
            </div>
        );
    }

    // CASE 2 eliminado: un usuario logueado SIN el rol de criador ya no ve una
    // pantalla de "acceso restringido" — cae directo al flujo normal de invitado
    // (CASE 3), donde puede registrarse como criador (eso crea una solicitud de
    // socio para la MISMA cuenta, no una cuenta nueva) o iniciar sesión con otra cuenta.

    // CASE 3: Invitado → portada (BreederPortalHome) o ingreso / registro, bajo la misma barra del portal
    return (
        <div className="bg-white min-h-screen overflow-x-hidden font-sans selection:bg-amber-400 selection:text-[#2e2418]">
            {/* Top Branded Navigation Header */}
            <nav className="fixed top-0 left-0 w-full bg-white/70 backdrop-blur-xl z-50 border-b border-amber-100/60 px-6 py-4 pt-[calc(1rem+env(safe-area-inset-top))]">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <button 
                        onClick={() => {
                            setActivePortalTab('discover');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} 
                        className="flex items-center gap-2.5 group"
                    >
                        <div className="bg-gradient-to-br from-[#d48a1c] to-[#3f7d5c] p-2.5 rounded-2xl text-white group-hover:rotate-12 transition-transform shadow-lg shadow-amber-100">
                            <ShieldCheck size={20} />
                        </div>
                        <div className="text-left">
                            <span className="font-bold text-[#2e2418] text-sm sm:text-base leading-none block whitespace-nowrap" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                                <span className="sm:hidden">Criaderos</span><span className="hidden sm:inline">Amigo Peludo Criaderos</span>
                            </span>
                            <span className="hidden sm:block text-[8px] font-bold text-amber-700 tracking-[0.25em] uppercase mt-0.5">Manada · Salud · Familia</span>
                        </div>
                    </button>

                    {/* Navigation Actions — visible en todos los tamaños */}
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        <button
                            onClick={() => setActivePortalTab('discover')}
                            className={`hidden sm:flex px-3 md:px-4 py-2 md:py-2.5 rounded-[7px] font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${
                                activePortalTab === 'discover'
                                    ? 'bg-amber-50 text-amber-800'
                                    : 'text-slate-500 hover:text-slate-800'
                            }`}
                        >
                            Descubrir
                        </button>
                        <button
                            onClick={() => setActivePortalTab('login')}
                            className={`px-3 sm:px-4 md:px-5 py-2 md:py-2.5 rounded-[7px] font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${
                                activePortalTab === 'login'
                                    ? 'bg-gradient-to-r from-[#d48a1c] to-[#3f7d5c] text-white shadow-lg shadow-amber-200'
                                    : 'border border-amber-100 text-[#4a3a26] hover:bg-amber-50'
                            }`}
                        >
                            Ingresar
                        </button>
                        <button
                            onClick={() => setActivePortalTab('register')}
                            className={`px-3 sm:px-4 md:px-5 py-2 md:py-2.5 rounded-[7px] font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${
                                activePortalTab === 'register'
                                    ? 'bg-gradient-to-r from-[#d48a1c] to-[#3f7d5c] text-white'
                                    : 'bg-[#2e2418] text-white hover:bg-[#3f7d5c]'
                            }`}
                        >
                            <span className="hidden sm:inline">Crear </span>Cuenta
                        </button>
                    </div>
                </div>
            </nav>

            {/* Portada (Descubrir): ocupa todo el ancho, con su propio hero bajo la barra fija */}
            {activePortalTab === 'discover' && (
                <BreederPortalHome
                    onRegister={() => { setActivePortalTab('register'); window.scrollTo({ top: 0 }); }}
                    onLogin={() => { setActivePortalTab('login'); window.scrollTo({ top: 0 }); }}
                />
            )}

            {/* Ingreso y registro */}
            <div className={`${activePortalTab === 'discover' ? 'hidden' : ''} pt-24 md:pt-28 pb-20 max-w-7xl mx-auto px-6 animate-in fade-in duration-300`}>
                {activePortalTab === 'login' && (
                    <div className="max-w-md mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <LoginView 
                            initialMode="BREEDER" 
                            hideRegister={true} 
                            onNavigate={handleInnerNavigate} 
                        />
                    </div>
                )}

                {activePortalTab === 'register' && (
                    <div className="max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <RegisterView 
                            role="breeder" 
                            onNavigate={handleInnerNavigate} 
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
export default BreederPortalView;
